/**
 * InfoScreenDisplay Component
 * Displays informational content during survey taking (welcome screens, instructions, etc.)
 * No response is required - user just reads and clicks continue.
 * Supports optional timed display where Continue button is disabled until timer completes.
 * Timer only starts after all media has loaded.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Info, ChevronRight, Clock, Loader2 } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';

// Helper to build full URL for media
const getFullMediaUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  return url;
};

interface InfoScreenDisplayProps {
  /** The information text to display */
  content: string;
  /** Optional description/additional context */
  description?: string;
  /** Callback when user clicks continue */
  onContinue: () => void;
  /** Whether the continue button is disabled */
  disabled?: boolean;
  /** Optional minimum display time in seconds before Continue button is enabled */
  displayTimeSeconds?: number;
  /** Optional media URLs to display */
  mediaUrls?: string[];
}

const InfoScreenDisplay: React.FC<InfoScreenDisplayProps> = ({
  content,
  description,
  onContinue,
  disabled = false,
  displayTimeSeconds,
  mediaUrls,
}) => {
  const totalImages = mediaUrls?.length || 0;

  // Track image loading - initialize based on props for immediate correct state
  const [loadedCount, setLoadedCount] = useState<number>(0);
  const [imageLoadingStates, setImageLoadingStates] = useState<boolean[]>(() =>
    new Array(mediaUrls?.length || 0).fill(false),
  );

  // Reset loading states whenever mediaUrls changes
  useEffect(() => {
    const newTotal = mediaUrls?.length || 0;
    setLoadedCount(0);
    setImageLoadingStates(new Array(newTotal).fill(false));
    setTimerStarted(false);
    if (displayTimeSeconds && displayTimeSeconds > 0) {
      setRemainingSeconds(displayTimeSeconds);
    }
  }, [mediaUrls, displayTimeSeconds]);

  // Images are loaded when all have loaded or there are none
  const imagesLoaded = totalImages === 0 || loadedCount >= totalImages;

  // Timer state
  const [remainingSeconds, setRemainingSeconds] = useState<number>(
    displayTimeSeconds && displayTimeSeconds > 0 ? displayTimeSeconds : 0,
  );
  const [timerStarted, setTimerStarted] = useState<boolean>(false);

  // Track individual image loading for UI feedback
  const handleImageLoad = useCallback((index: number) => {
    setImageLoadingStates((prev) => {
      if (index >= prev.length) return prev;
      const newStates = [...prev];
      newStates[index] = true;
      return newStates;
    });
    setLoadedCount((prev) => prev + 1);
  }, []);

  const handleImageError = useCallback((index: number) => {
    // Count errors as "loaded" so we don't wait forever
    setImageLoadingStates((prev) => {
      if (index >= prev.length) return prev;
      const newStates = [...prev];
      newStates[index] = true;
      return newStates;
    });
    setLoadedCount((prev) => prev + 1);
  }, []);

  // Start timer when images are loaded
  useEffect(() => {
    if (imagesLoaded && !timerStarted && displayTimeSeconds && displayTimeSeconds > 0) {
      setTimerStarted(true);
      setRemainingSeconds(displayTimeSeconds);
    }
  }, [imagesLoaded, timerStarted, displayTimeSeconds]);

  // Countdown timer
  useEffect(() => {
    if (!timerStarted || remainingSeconds <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timerStarted, remainingSeconds]);

  // Timer is complete when: no timer set, OR timer started and reached 0
  const timerCompleted =
    !displayTimeSeconds || displayTimeSeconds <= 0 || (timerStarted && remainingSeconds <= 0);

  // Button is disabled if: external disabled, OR images not loaded, OR timer not completed
  const isButtonDisabled = disabled || !imagesLoaded || !timerCompleted;

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-teal-50 border border-teal-200 rounded-lg">
        <Info className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-teal-700">
          {!imagesLoaded ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading content... ({loadedCount}/{totalImages})
            </span>
          ) : displayTimeSeconds && displayTimeSeconds > 0 && remainingSeconds > 0 ? (
            <>
              Please read the information below carefully. You can continue in{' '}
              <span className="font-semibold">{remainingSeconds}</span> second
              {remainingSeconds !== 1 ? 's' : ''}.
            </>
          ) : (
            <>Please read the information below, then click "Continue" to proceed.</>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="prose prose-gray max-w-none">
          {/* Main text - render as paragraphs if multiline */}
          <div className="text-gray-800 text-base leading-relaxed whitespace-pre-wrap">
            {content}
          </div>

          {/* Optional description */}
          {description && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
            </div>
          )}
        </div>

        {/* Media Display */}
        {mediaUrls && mediaUrls.length > 0 && (
          <div className="mt-6 space-y-4">
            <div
              className={`grid gap-4 ${mediaUrls.length === 1 ? 'grid-cols-1 max-w-2xl mx-auto' : 'grid-cols-1 md:grid-cols-2'}`}
            >
              {mediaUrls.map((url, index) => {
                const fullUrl = getFullMediaUrl(url);
                const isVideo =
                  url &&
                  (url.includes('youtube.com') ||
                    url.includes('youtu.be') ||
                    url.includes('vimeo.com') ||
                    /\.(mp4|mov|avi|webm|mkv)(\?|$)/i.test(url));
                // Show loading if state not yet initialized or not loaded
                const isLoaded = imageLoadingStates[index] === true;

                return (
                  <div
                    key={index}
                    className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50 min-h-[200px]"
                  >
                    {/* Loading skeleton - always show until explicitly loaded */}
                    {!isLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100 animate-pulse z-10">
                        <div className="text-center">
                          <Loader2 className="w-8 h-8 text-teal-500 animate-spin mx-auto mb-2" />
                          <span className="text-sm text-gray-500">
                            Loading image {index + 1}...
                          </span>
                        </div>
                      </div>
                    )}

                    {isVideo ? (
                      url.includes('youtube.com') || url.includes('youtu.be') ? (
                        <div className="aspect-video">
                          <iframe
                            src={
                              url.includes('youtube.com')
                                ? url.replace('watch?v=', 'embed/')
                                : url.replace('youtu.be/', 'youtube.com/embed/')
                            }
                            className={`w-full h-full transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                            allowFullScreen
                            title={`Video ${index + 1}`}
                            onLoad={() => handleImageLoad(index)}
                            onError={() => handleImageError(index)}
                          />
                        </div>
                      ) : (
                        <video
                          src={fullUrl}
                          className={`w-full h-auto max-h-[400px] transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                          controls
                          onLoadedData={() => handleImageLoad(index)}
                          onError={() => handleImageError(index)}
                        />
                      )
                    ) : (
                      <img
                        src={fullUrl}
                        alt={`Info screen media ${index + 1}`}
                        className={`w-full h-auto max-h-[400px] object-contain transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                        onLoad={() => handleImageLoad(index)}
                        onError={() => handleImageError(index)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Continue Button with Timer */}
      <div className="flex justify-end pt-2">
        <button
          onClick={onContinue}
          disabled={isButtonDisabled}
          className={`
            inline-flex items-center gap-2 px-6 py-3 
            font-medium rounded-lg transition-all
            ${
              isButtonDisabled
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-teal-500 hover:bg-teal-600 text-white shadow-sm hover:shadow-md active:scale-[0.98]'
            }
          `}
        >
          {!imagesLoaded ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading...</span>
            </>
          ) : displayTimeSeconds && displayTimeSeconds > 0 && remainingSeconds > 0 ? (
            <>
              <Clock className="w-4 h-4 animate-pulse" />
              <span>Wait {remainingSeconds}s</span>
            </>
          ) : (
            <>
              Continue
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default InfoScreenDisplay;
